import api from './api';

export interface Bug {
  id: number;
  title: string;
  description: string;
  type: 'bug';
  status: 'todo' | 'in_progress' | 'in_review' | 'testing' | 'done' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  project_id: number;
  project?: {
    id: number;
    name: string;
  };
  assigned_to?: number;
  assignedUser?: {
    id: number;
    name: string;
    email: string;
  };
  created_by: number;
  estimated_hours?: number;
  actual_hours?: number;
  due_date?: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    steps_to_reproduce?: string;
    expected_behavior?: string;
    actual_behavior?: string;
    environment?: string;
  };
  comments?: any[];
  timeLogs?: any[];
}

export interface BugStatistics {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  critical: number;
  high: number;
  by_project: Array<{
    project_id: number;
    project_name: string;
    count: number;
  }>;
  recent_count: number;
}

export interface BugFilters {
  project_id?: number;
  status?: string;
  severity?: string;
  assigned_to?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

export interface CreateBugRequest {
  title: string;
  description: string;
  project_id: number;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status?: 'todo' | 'in_progress' | 'in_review' | 'testing' | 'done';
  assigned_to?: number;
  estimated_hours?: number;
  due_date?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  environment?: string;
}

export interface UpdateBugRequest {
  title?: string;
  description?: string;
  project_id?: number;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  status?: 'todo' | 'in_progress' | 'in_review' | 'testing' | 'done' | 'cancelled';
  assigned_to?: number;
  estimated_hours?: number;
  due_date?: string;
}

class BugService {
  private baseUrl = '/api/v1/developer/bugs';

  /**
   * Get all bugs with optional filters
   */
  async getBugs(filters?: BugFilters): Promise<{ data: Bug[]; meta: any }> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const response = await api.get(`${this.baseUrl}?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get bugs assigned to current user
   */
  async getMyBugs(filters?: Pick<BugFilters, 'status' | 'sort_by' | 'sort_order' | 'per_page' | 'page'>): Promise<{ data: Bug[]; meta: any }> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const response = await api.get(`${this.baseUrl}/my-bugs?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get bug statistics
   */
  async getStatistics(userId?: number): Promise<BugStatistics> {
    const params = userId ? `?user_id=${userId}` : '';
    const response = await api.get(`${this.baseUrl}/statistics${params}`);
    return response.data.data;
  }

  /**
   * Get single bug details
   */
  async getBug(id: number): Promise<Bug> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  /**
   * Create new bug report
   */
  async createBug(data: CreateBugRequest): Promise<Bug> {
    const response = await api.post(this.baseUrl, data);
    return response.data.data;
  }

  /**
   * Update bug
   */
  async updateBug(id: number, data: UpdateBugRequest): Promise<Bug> {
    const response = await api.patch(`${this.baseUrl}/${id}`, data);
    return response.data.data;
  }

  /**
   * Update bug status
   */
  async updateBugStatus(id: number, status: Bug['status']): Promise<Bug> {
    const response = await api.patch(`${this.baseUrl}/${id}/status`, { status });
    return response.data.data;
  }

  /**
   * Delete bug
   */
  async deleteBug(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get severity color for UI display
   */
  getSeverityColor(priority: Bug['priority']): 'error' | 'warning' | 'info' | 'default' {
    switch (priority) {
      case 'urgent':
      case 'high':
        return 'error';
      case 'normal':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  }

  /**
   * Get status color for UI display
   */
  getStatusColor(status: Bug['status']): 'default' | 'primary' | 'success' | 'info' | 'warning' | 'error' {
    switch (status) {
      case 'done':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'in_review':
      case 'testing':
        return 'info';
      case 'todo':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  }
}

export default new BugService();
